import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBooks,
  faCloudUpload,
  faDiamondExclamation,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import { SafeUrlPipe } from '@frontend/util/pipes';
import { FileErrorLogoSVG, HTML5SVG, MusicSVG } from '@shared-assets/images';
import { BannerService } from '@terminus-ui/banner';
import { SvgComponent } from '@terminus-ui/svg';
import { AuthService, GetMarketerResponse } from 'auth';
import {
  AddAssetToLibraryRequest,
  AddAssetToLibraryResponse,
  AssetType,
  CreativeAssetLibraryApi,
  Dimensions,
  DisplayInformationFactory,
  getDisplayInformationPropAndFactory,
  V1Asset,
  V1AssetFactory,
  V1AssetVersionFactory,
} from 'creative-asset-library-api';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessagesModule } from 'primeng/messages';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import {
  catchError,
  combineLatest,
  delay,
  EMPTY,
  expand,
  filter,
  firstValueFrom,
  last,
  map,
  mergeMap,
  Observable,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  SlermsDownloadResponse,
  SlermsFileInfo,
  SlermsScanResult,
  SlermsService,
} from 'slerms-api';
import { SplitIoService } from 'split-io';

export type Creative = {
  asset: V1Asset;
  validationError: string;
  uploadError: string;
  uploadProgress: number;
  getDownload404: boolean;
  completed: boolean;
};

export type CreativeType = 'Image' | 'CTV' | 'Video' | 'Audio';

@Component({
  selector: 'lib-creative-uploader',
  imports: [
    CommonModule,
    FontAwesomeModule,
    DialogModule,
    ButtonModule,
    TableModule,
    ProgressBarModule,
    SvgComponent,
    ProgressSpinnerModule,
    TabViewModule,
    SafeUrlPipe,
    MessagesModule,
  ],
  templateUrl: './creative-uploader.component.html',
  styleUrl: './creative-uploader.component.scss',
})
export class CreativeUploaderComponent implements OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  private slermsService = inject(SlermsService);
  private creativeAssetLibraryApi = inject(CreativeAssetLibraryApi);
  private messageService = inject(MessageService);
  private bannerService = inject(BannerService);

  externalIcon = faExternalLink;
  fileError = FileErrorLogoSVG;
  trashIcon = faTrash;
  uploadIcon = faCloudUpload;
  libraryIcon = faBooks;
  diamondExclamation = faDiamondExclamation;
  HTML5Svg = HTML5SVG;
  MusicSvg = MusicSVG;

  tabs: Map<CreativeType, Creative[]> = new Map([
    ['Image', []],
    ['CTV', []],
    ['Video', []],
    ['Audio', []],
  ]);

  // default open on first tab (Image)
  activeIndex = 0;

  audioCreativeInfo: ToastMessageOptions[] = [
    {
      severity: 'info',
      detail:
        'Audio Creatives require both an audio file and a companion banner. Upload the audio file here and assign a banner in the asset library',
    },
  ];

  audioFlag$: Observable<boolean> = inject(SplitIoService)
    .getSplitTreatment$('adx_audio_enabled')
    .pipe(map((treatment) => (treatment === 'on' ? true : false)));

  // preserve original property order for tab map above
  origOrder(a, b) {
    return 1;
  }

  @Input() showUploader = false;
  @Input() set openingTabIndex(index: number) {
    if (index) {
      // small delay due to split flag check
      setTimeout(() => (this.activeIndex = index), 1000);
    }
  }
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onClose = new EventEmitter();

  @Input() creativeUploaded: boolean;
  @Output() creativeUploadedChanged = new EventEmitter<boolean>();
  @Output() audioBannerUploaded = new EventEmitter<void>();
  @ViewChildren('fileInput') fileInput: QueryList<ElementRef>;

  constructor() {
    this.triggerUploadToast();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  resetTabs() {
    this.tabs.set('Image', []);
    this.tabs.set('CTV', []);
    this.tabs.set('Video', []);
    this.tabs.set('Audio', []);
  }

  openFileInput(tab: CreativeType | string) {
    // find the fileInput on the current tab
    const fileInputElement = this.fileInput
      .toArray()
      .find((input) => (input.nativeElement.id = `fi-${tab}`))?.nativeElement;
    fileInputElement?.click();
    // null out value to allow user to upload same file again
    // else, (change) might not be triggered
    fileInputElement.value = null;
  }

  removeCreative(creative: Creative, creatives: Creative[]) {
    creatives.splice(creatives.indexOf(creative), 1);
    if (
      !creative.completed ||
      creative.uploadError ||
      creative.validationError
    ) {
      return;
    } else {
      this.creativeAssetLibraryApi
        .setAssetLibraryVisibility({
          assetId: creative.asset.id,
          visibleInLibrary: false,
        })
        .pipe(
          take(1),
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe();
    }
  }

  safeUrl(url: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  safeResourceUrl(url: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public selectAudioBannerFile(event: any, selectedAudioAssetId: string) {
    const files = event.target.files;
    if (!files || files.length < 1) {
      this.messageService.add({
        key: 'library-toast',
        severity: 'error',
        sticky: true,
        closable: true,
        detail:
          'Invalid file for companion banner. <a href="https://support.terminus.com/hc/en-us/articles/4406213411603-A-Guide-to-Audio-Ads-in-Ad-Experiences" target="_blank">See this article</a> for accepted sizes and file types.',
      });
    } else {
      const file = files[0];
      if (!this.isImage(file)) {
        this.messageService.add({
          key: 'library-toast',
          severity: 'error',
          sticky: true,
          closable: true,
          detail:
            'Invalid file type for companion banner. <a href="https://support.terminus.com/hc/en-us/articles/4406213411603-A-Guide-to-Audio-Ads-in-Ad-Experiences" target="_blank">See this article</a> for accepted sizes and file types.',
        });
      } else {
        const creative: Creative = {
          asset: {
            ...V1AssetFactory('', {
              fileName: file.name,
              assetType: 'IMAGE_ASSET',
            }),
          },
          validationError: '',
          uploadError: '',
          uploadProgress: 0,
          getDownload404: false,
          completed: false,
        };
        this.messageService.add({
          key: 'library-toast',
          severity: 'info',
          detail: 'Audio Companion Banner upload in progress.',
          life: 5000,
        });
        this.createCreative(file, creative).then((result) => {
          this.creativeAssetLibraryApi
            .updateAssociatedAsset({
              assetId: selectedAudioAssetId,
              associatedAssetId: result.asset.id,
            })
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.messageService.add({
                  key: 'library-toast',
                  severity: 'success',
                  detail:
                    '<p class="align-self-center">Audio Companion Banner uploaded successfully!</p><p class="align-self-center">Your completed Audio Creative will be visible in the library shortly.</p>',
                  life: 5000,
                });
                // emit to tell library to refresh its report
                this.audioBannerUploaded.emit();
              },
              error: () => {
                this.messageService.add({
                  key: 'library-toast',
                  severity: 'error',
                  detail: 'Error uploading companion banner. Please try again.',
                  sticky: true,
                  closable: true,
                });
              },
            });
        });
      }
    }
  }

  public async selectFiles(event: any, dropOrBrowse: 'drop' | 'browse') {
    event.preventDefault();
    event.stopPropagation();

    // get the tab(creative type) we're on from the input's id
    const tab = event.target.id.split('-')[1];

    const files =
      dropOrBrowse === 'drop' ? event.dataTransfer?.files : event.target?.files;

    if (files && files.length > 0) {
      const fileList: File[] = Array.from(files);
      const creativesToBeUploaded: Promise<Creative>[] = [];
      for (const file of fileList) {
        const creative: Creative = {
          asset: {
            ...V1AssetFactory('', {
              fileName: file.name,
              assetType: this.getAssetType(file.type, tab),
            }),
          },
          validationError: '',
          uploadError: '',
          uploadProgress: 0,
          getDownload404: false,
          completed: false,
        };
        this.tabs.get(tab)?.push(creative);
        const validation = await this.validateFile(file, tab, creative);
        if (validation === true) {
          creativesToBeUploaded.push(this.createCreative(file, creative));
        }
      }
      Promise.all(creativesToBeUploaded).then((results) => {
        console.debug('Upload results:', results);
        this.creativeUploadedChanged.emit(true);
      });
    } else {
      throw new Error('Error reading files');
    }
  }

  private async validateFile(
    file: File,
    tab: CreativeType,
    creative: Creative,
  ): Promise<boolean> {
    let correctTab = false;
    switch (tab) {
      case 'Image':
        correctTab = this.isImage(file) || this.isHtml5(file);
        break;
      case 'CTV':
        correctTab = this.isCTV(file);
        break;
      case 'Video':
        correctTab = this.isVideo(file);
        break;
      case 'Audio':
        correctTab = this.isAudio(file);
        break;
      default:
        correctTab = false;
        break;
    }
    if (!correctTab) {
      creative.validationError = `${
        file.type
      } is not a valid file type for ${tab.toUpperCase()}. Please try another file.`;
      creative.completed = true;
      return false;
    } else if (!this.isValidFileType(file)) {
      creative.validationError = `Unsupported file type (${file.type}).`;
      creative.completed = true;
      return false;
    } else if (!(await this.isValidDimensions(file))) {
      creative.validationError = `Invalid file dimensions for ${file.name}.`;
      creative.completed = true;
      return false;
    } else if (!this.isValidFileSize(file, tab)) {
      creative.validationError = `File size is too large (${Math.round(
        file.size / 1024,
      )} kB).`;
      creative.completed = true;
      return false;
    } else {
      return true;
    }
  }

  isOneOfTypes(types: string[], file: File): boolean {
    return types.some((type) => file.type.includes(type));
  }

  isImage(file: File): boolean {
    return this.isOneOfTypes(
      ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/gif'],
      file,
    );
  }

  isHtml5(file: File): boolean {
    return this.isOneOfTypes(
      ['application/zip', 'application/x-zip-compressed'],
      file,
    );
  }

  isVideo(file: File): boolean {
    return this.isOneOfTypes(
      ['video/mp4', 'video/flv', 'video/mov', 'video/mpeg', 'video/webm'],
      file,
    );
  }

  isAudio(file: File): boolean {
    return this.isOneOfTypes(['audio/mp4', 'audio/mpeg', 'audio/wav'], file);
  }

  isCTV(file: File): boolean {
    return this.isOneOfTypes(['video/mp4'], file);
  }

  private isValidFileSize(file: File, tab: CreativeType): boolean {
    let assetType = this.getAssetType(file.type, tab);

    switch (assetType) {
      case 'IMAGE_ASSET':
        return file.size < 800 * 1024; // 800 KB
      case 'CTV_ASSET':
        return file.size < 10 * 1024 ** 3; // 10 GB
      case 'VIDEO_ASSET':
        return file.size < 200 * 1024 ** 2; // 200 MB
      case 'AUDIO_ASSET':
        return file.size < 10 * 1024 ** 2; // 10 MB
      case 'HTML5_ASSET':
        return file.size < 5 * 1024 ** 2; // 5 MB
      case 'UNSET':
        return file.size < 800 * 1024; // 800 KB
      default:
        return file.size < 800 * 1024; // 800 KB
    }
  }

  private isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'video/mp4',
      'video/flv',
      'video/mov',
      'video/mpeg',
      'video/webm',
      'audio/mp4',
      'audio/mpeg',
      'audio/wav',
      'application/zip',
      'application/x-zip-compressed',
    ];
    return allowedTypes.includes(file.type);
  }

  isValidDimensions(file: File): Promise<boolean> {
    const allowedImageDimensions = [
      { width: 88, height: 31 },
      { width: 120, height: 240 },
      { width: 120, height: 60 },
      { width: 120, height: 600 },
      { width: 120, height: 90 },
      { width: 125, height: 125 },
      { width: 125, height: 83 },
      { width: 160, height: 600 },
      { width: 180, height: 150 },
      { width: 180, height: 500 },
      { width: 226, height: 850 },
      { width: 230, height: 230 },
      { width: 230, height: 600 },
      { width: 234, height: 60 },
      { width: 240, height: 400 },
      { width: 250, height: 250 },
      { width: 250, height: 360 },
      { width: 300, height: 100 },
      { width: 300, height: 1050 },
      { width: 300, height: 240 },
      { width: 300, height: 60 },
      { width: 300, height: 50 },
      { width: 300, height: 250 },
      { width: 300, height: 600 },
      { width: 320, height: 160 },
      { width: 320, height: 240 },
      { width: 320, height: 250 },
      { width: 320, height: 320 },
      { width: 320, height: 480 },
      { width: 320, height: 80 },
      { width: 320, height: 50 },
      { width: 336, height: 280 },
      { width: 400, height: 400 },
      { width: 440, height: 220 },
      { width: 450, height: 250 },
      { width: 468, height: 400 },
      { width: 468, height: 60 },
      { width: 480, height: 250 },
      { width: 480, height: 280 },
      { width: 480, height: 320 },
      { width: 480, height: 80 },
      { width: 519, height: 225 },
      { width: 544, height: 225 },
      { width: 550, height: 340 },
      { width: 551, height: 289 },
      { width: 555, height: 111 },
      { width: 555, height: 333 },
      { width: 600, height: 75 },
      { width: 640, height: 480 },
      { width: 720, height: 300 },
      { width: 720, height: 480 },
      { width: 728, height: 90 },
      { width: 750, height: 200 },
      { width: 768, height: 1024 },
      { width: 800, height: 250 },
      { width: 930, height: 180 },
      { width: 960, height: 325 },
      { width: 960, height: 60 },
      { width: 970, height: 250 },
      { width: 970, height: 66 },
      { width: 970, height: 90 },
      { width: 975, height: 300 },
      { width: 980, height: 120 },
      { width: 980, height: 150 },
      { width: 980, height: 240 },
      { width: 980, height: 250 },
      { width: 980, height: 400 },
      { width: 980, height: 90 },
      { width: 994, height: 250 },
      { width: 1000, height: 90 },
      { width: 1020, height: 250 },
      { width: 1024, height: 768 },
      { width: 1280, height: 100 },
    ];

    const allowedVideoDimensions = [
      { width: 480, height: 360 },
      { width: 640, height: 360 },
      { width: 640, height: 480 },
      { width: 1280, height: 720 },
      { width: 1920, height: 1080 },
    ];

    return new Promise((resolve, reject) => {
      if (this.isHtml5(file) || this.isAudio(file)) {
        resolve(true);
      }

      if (this.isVideo(file)) {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        video.src = url;

        video.onloadeddata = () => {
          URL.revokeObjectURL(url);
          const isValidDimensions = allowedVideoDimensions.some(
            (dimensions) => {
              return (
                dimensions.width === video.videoWidth &&
                dimensions.height === video.videoHeight
              );
            },
          );

          return resolve(isValidDimensions);
        };

        video.onerror = function () {
          reject(false);
        };
      } else if (this.isImage(file)) {
        const image = new Image();
        image.src = URL.createObjectURL(file);

        image.onload = function () {
          const isValidDimensions = allowedImageDimensions.some(
            (dimensions) => {
              return (
                dimensions.width === image.width &&
                dimensions.height === image.height
              );
            },
          );

          return resolve(isValidDimensions);
        };

        image.onerror = function () {
          reject(false);
        };
      }
    });
  }

  private getAssetType(mimeType: string, tab: CreativeType): AssetType {
    const creativeTypesMap: Record<string, AssetType> = {
      'image/png': 'IMAGE_ASSET',
      'image/jpg': 'IMAGE_ASSET',
      'image/jpeg': 'IMAGE_ASSET',
      'image/gif': 'IMAGE_ASSET',
      'video/mp4': 'VIDEO_ASSET',
      'video/flv': 'VIDEO_ASSET',
      'video/mov': 'VIDEO_ASSET',
      'video/mpeg': 'VIDEO_ASSET',
      'video/webm': 'VIDEO_ASSET',
      'audio/mp4': 'AUDIO_ASSET',
      'audio/mpeg': 'AUDIO_ASSET',
      'audio/wav': 'AUDIO_ASSET',
      'application/zip': 'HTML5_ASSET',
      'application/x-zip-compressed': 'HTML5_ASSET',
    };

    return mimeType === 'video/mp4' && tab === 'CTV'
      ? 'CTV_ASSET'
      : creativeTypesMap[mimeType] || 'UNSET';
  }

  private async createCreative(
    file: File,
    creative: Creative,
  ): Promise<Creative> {
    return firstValueFrom(
      combineLatest({
        authToken: this.authService.myTerminusToken$,
        marketer: this.authService.myTerminusMarketer$,
      }).pipe(
        take(1),
        switchMap(({ authToken, marketer }) =>
          this.registerFile(authToken, file, marketer, creative),
        ),
        switchMap(({ authToken, fileInfo }) =>
          this.uploadRegisteredFileWithProgress(
            authToken,
            fileInfo,
            file,
            creative,
          ),
        ),
        delay(750),
        switchMap(({ authToken, fileInfo, creative }) =>
          this.notifyUploaded(authToken, fileInfo, creative),
        ),
        switchMap(({ authToken, fileInfo }) =>
          this.getDownload(authToken, fileInfo.file_uuid, creative, file),
        ),
        switchMap(({ downloadResp }) =>
          this.addAssetToAdLibrary(downloadResp, creative),
        ),
        switchMap(({ addAssetResp, download, creative }) =>
          this.finalizeAsset(
            file,
            addAssetResp.assetId,
            download.file_mime_type,
            creative,
          ),
        ),
      ),
    );
  }

  private registerFile(
    authToken: string,
    file: File,
    marketer: GetMarketerResponse,
    creative: Creative,
  ): Observable<{
    authToken: string;
    fileInfo: SlermsFileInfo;
    creative: Creative;
  }> {
    return this.slermsService
      .registerFile(authToken, file.name, file.type, [
        {
          claim: 'sub.svc',
          value: 'creative_asset_library',
        },
        {
          claim: 'sub.mar',
          value: marketer!.uuid,
        },
      ])
      .pipe(
        map((fileInfo) => ({ authToken, fileInfo, creative })),
        catchError((err) => {
          creative.uploadError = 'Error uploading asset.';
          creative.uploadProgress = 0;
          creative.completed = true;
          throw err;
        }),
      );
  }

  private uploadRegisteredFileWithProgress(
    authToken: string,
    fileInfo: SlermsFileInfo,
    file: File,
    creative: Creative,
  ): Observable<{
    authToken: string;
    fileInfo: SlermsFileInfo;
    creative: Creative;
  }> {
    return this.slermsService
      .uploadRegisteredFileWithProgress(fileInfo.drop_box_url, file, file.type)
      .pipe(
        tap((uploadEvent) => {
          if (
            'type' in uploadEvent &&
            uploadEvent.type === HttpEventType.UploadProgress
          ) {
            creative.uploadProgress = Math.round(
              (uploadEvent.loaded / (uploadEvent.total || 1)) * 100,
            );
          }
        }),
        filter(
          (uploadEvent) =>
            'type' in uploadEvent &&
            uploadEvent.type === HttpEventType.Response,
        ),
        map(() => {
          creative.uploadProgress === 100;
          return { authToken, fileInfo, creative };
        }),
        catchError((err) => {
          creative.uploadError = 'Error uploading asset.';
          creative.uploadProgress = 0;
          creative.completed = true;
          throw err;
        }),
      );
  }

  private getDownload(
    authToken: string,
    fileUuid: string,
    creative: Creative,
    file: File,
  ): Observable<{
    downloadResp: SlermsDownloadResponse;
  }> {
    return this.slermsService.getDownload(authToken, fileUuid).pipe(
      mergeMap((downloadResp) => {
        if (
          [
            SlermsScanResult.NOT_SCANNED,
            SlermsScanResult.SCAN_FAILED,
            SlermsScanResult.MALICIOUS,
          ].includes(downloadResp.scan_result)
        ) {
          return throwError(() => new Error(downloadResp.scan_result));
        } else if (
          [SlermsScanResult.CLEAN, SlermsScanResult.SYSTEM_BYPASSED].includes(
            downloadResp.scan_result,
          )
        ) {
          creative.completed = true;
          return of({ downloadResp });
        } else {
          return throwError(() => new Error('UNKNOWN'));
        }
      }),
      retry({
        count: 30,
        delay: (
          err: Observable<HttpErrorResponse | Error>,
          retryCount: number,
        ) => {
          if (
            err instanceof Error &&
            err.message === SlermsScanResult.NOT_SCANNED
          ) {
            if (retryCount >= 30) {
              creative.uploadError =
                'Virus scan failed, max retries exceeded. Please check the provided file.';
              creative.uploadProgress = 0;
              creative.completed = true;
              return throwError(() => err);
            } else {
              return timer(3000);
            }
          } else if (
            err instanceof Error &&
            [
              SlermsScanResult.SCAN_FAILED,
              SlermsScanResult.MALICIOUS,
              'UNKNOWN',
            ].includes(err.message)
          ) {
            creative.uploadError =
              err.message === SlermsScanResult.SCAN_FAILED
                ? 'Virus scan failed. Please check the provided file and try again.'
                : 'Security warning: upload blocked.';
            creative.uploadProgress = 0;
            creative.completed = true;
            return throwError(() => err);
          } else if (err instanceof HttpErrorResponse && err.status == 404) {
            // retry entire upload process once on 404
            if (!creative.getDownload404) {
              creative.getDownload404 = true;
              creative.uploadProgress = 0;
              this.createCreative(file, creative);
              return EMPTY;
            } else {
              creative.uploadError = 'Error uploading file.';
              creative.uploadProgress = 0;
              creative.completed = true;
              return throwError(() => err);
            }
          } else {
            creative.uploadError =
              'Unable to upload file. Please check the provided file and try again.';
            creative.uploadProgress = 0;
            creative.completed = true;
            return throwError(() => err);
          }
        },
      }),
    );
  }

  private notifyUploaded(
    authToken: string,
    fileInfo: SlermsFileInfo,
    creative: Creative,
  ): Observable<{
    authToken: string;
    fileInfo: SlermsFileInfo;
    creative: Creative;
  }> {
    return this.slermsService
      .notifyUploaded(authToken, fileInfo.file_uuid)
      .pipe(
        map(() => ({ authToken, fileInfo, creative })),
        catchError((err) => {
          creative.uploadError = 'Error uploading asset. Please try again.';
          creative.uploadProgress = 0;
          creative.completed = true;
          throw err;
        }),
      );
  }

  private addAssetToAdLibrary(
    download: SlermsDownloadResponse,
    creative: Creative,
  ): Observable<{
    addAssetResp: AddAssetToLibraryResponse;
    download: SlermsDownloadResponse;
    creative: Creative;
  }> {
    const request: AddAssetToLibraryRequest = {
      slermsFileUuid: download.file_uuid,
      fileName: download.file_display_name as string,
      mimeType: download.file_mime_type,
      assetType: creative.asset.assetType,
      tags: {},
      expectedFileSizeInBytes: download.content_length,
      ownerIds: {},
    };
    return this.creativeAssetLibraryApi.addAssetToLibrary(request).pipe(
      map((addAssetResp) => ({ download, addAssetResp, creative })),
      catchError((err) => {
        creative.uploadError = 'Error adding asset to library.';
        creative.uploadProgress = 0;
        creative.completed = true;
        throw err;
      }),
    );
  }

  private finalizeAsset(
    file: File,
    assetId: string,
    mimeType: string,
    creative: Creative,
  ): Observable<Creative> {
    const assetType = creative.asset.assetType;
    return this.readFileThumbnail(
      file,
      assetId,
      creative.asset.assetType,
      document,
    ).pipe(
      map((displayInfo) => {
        const { prop, factory } =
          getDisplayInformationPropAndFactory(assetType);
        const dimensions: Dimensions = {
          width: displayInfo.width,
          height: displayInfo.height,
        };
        const assetDisplayInfo = {
          [prop]: factory(displayInfo.src, dimensions),
        };
        const version = V1AssetVersionFactory(assetId, {
          ...DisplayInformationFactory(assetDisplayInfo),
        });

        creative.asset.mimeType = mimeType;
        creative.asset.assetType = assetType;
        creative.asset.tags = {};
        creative.asset.version = version;
        creative.asset.id = assetId;
        creative.asset.version.customerFacingStatusMessage =
          displayInfo.status || '';
        creative.asset.ready = true;

        return creative;
      }),
      catchError((err) => {
        creative.uploadError = 'Error uploading asset.';
        creative.uploadProgress = 0;
        creative.completed = true;
        throw err;
      }),
    );
  }

  public readFileThumbnail(
    file: File,
    assetId: string,
    type: AssetType,
    document: Document,
  ): Observable<any> {
    const thumbnailInfo: Record<AssetType, () => Observable<any>> = {
      ['IMAGE_ASSET']: () =>
        new Observable((observer) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
              observer.next({
                src: reader.result as string,
                width: img.width,
                height: img.height,
              });
            };
            img.src = reader.result as string;
          };

          reader.readAsDataURL(file);
        }),
      ['CTV_ASSET']: () =>
        new Observable((observer) => {
          const reader = new FileReader();

          reader.onload = () => {
            const video = document.createElement('video');
            const loadedmetadataHandler = () => {
              observer.next({
                src: reader.result as string,
                width: video.videoWidth,
                height: video.videoHeight,
              });

              video.removeEventListener(
                'loadedmetadata',
                loadedmetadataHandler,
              );
            };

            video.addEventListener('loadedmetadata', loadedmetadataHandler);

            video.src = reader.result as string;
          };

          reader.readAsDataURL(file);
        }),
      ['VIDEO_ASSET']: () =>
        new Observable((observer) => {
          const reader = new FileReader();

          reader.onload = () => {
            const video = document.createElement('video');
            const loadedmetadataHandler = () => {
              observer.next({
                src: reader.result as string,
                width: video.videoWidth,
                height: video.videoHeight,
              });

              video.removeEventListener(
                'loadedmetadata',
                loadedmetadataHandler,
              );
            };

            video.addEventListener('loadedmetadata', loadedmetadataHandler);

            video.src = reader.result as string;
          };

          reader.readAsDataURL(file);
        }),
      ['HTML5_ASSET']: () => {
        return this.creativeAssetLibraryApi
          .getAssetById({ assetIds: [assetId] })
          .pipe(
            expand((resp) => {
              const isPending =
                resp.assets[0].version?.customerFacingStatusMessage ===
                'PENDING';
              return isPending
                ? this.creativeAssetLibraryApi
                    .getAssetById({ assetIds: [assetId] })
                    .pipe(delay(2000))
                : EMPTY;
            }),
            take(7), // limit to 7 attempts
            last(), // emit the last response, whether the status is 'PENDING' or not
            map((resp: any) => {
              return {
                src: resp.assets[0].version.html5Display.previewSrc,
                width: resp.assets[0].version.html5Display.widthPx,
                height: resp.assets[0].version.html5Display.heightPx,
                status: resp.assets[0].version.customerFacingStatusMessage,
              };
            }),
          );
      },
      // TODO: confirm these properties are correct, currently guessed
      ['AUDIO_ASSET']: () => {
        return this.creativeAssetLibraryApi
          .getAssetById({ assetIds: [assetId] })
          .pipe(
            map((resp: any) => {
              return {
                src: resp.assets[0].version.audioDisplay.previewSrc,
                width: resp.assets[0].version.audioDisplay.widthPx,
                height: resp.assets[0].version.audioDisplay.heightPx,
              };
            }),
            take(1),
          );
      },
      ['UNSET']: () =>
        of({
          src: '',
          width: 0,
          height: 0,
        }),
    };

    return thumbnailInfo[type]();
  }

  triggerUploadToast() {
    this.messageService.add({
      severity: 'info',
      detail:
        'Audio Creatives require both an audio file and a companion banner. Upload the audio file here and assign a banner in the asset library',
    });
  }

  // try to prevent memory leaks
  ngOnDestroy() {
    this.onClose.unsubscribe();
  }
}
